import { useEffect } from 'react'

/**
 * Executes a callback when browser events fire outside
 * a list of DOM nodes referenced by an array of React refs.
 *
 * The `refs` argument should be memoized with useMemo.
 * The `callback` argument should have its reference memoized with useCallback.
 *
 * e.g.
 *
 * useCallbackOnEventOutside(
 *   useMemo(() => [refA, refB], [refA, refB]),
 *   useCallback(() => {
 *     setIsMenuOpen(false)
 *   }, [setIsMenuOpen])
 * )
 *
 * @param {Array} refs An array of refs from useRef()
 * @param {Function} callback A function to execute when events happen outside the refs
 * @param {String} event The name of the DOM event to register a callback for
 */
const useCallbackOnEventOutside = (refs, callback, event = 'mousedown') => {
  useEffect(() => {
    const handleOnEvent = evt => {
      if (
        refs.every(ref => ref.current) &&
        refs.every(ref => !ref.current.contains(evt.target))
      ) {
        callback(evt)
      }
    }

    window.addEventListener(event, handleOnEvent)

    return () => window.removeEventListener(event, handleOnEvent)
  }, [refs, callback, event])
}

export { useCallbackOnEventOutside }
